import { useCallback, useState } from 'react';
import { useSellerDataUpdater, useSellerData, SellerPageFederatedSeller } from '@fiverr-private/seller_pages_toolbox';
import { useSellerRoles, useSellerRolesUpdater } from './useSellerRoles';

export const useUGCData = () => {
    const [showTranslatedUGC, setShowTranslatedUGC] = useState(false);

    const seller = useSellerData(selectSellerSlice);
    const setSeller = useSellerDataUpdater();

    const sellerRoles = useSellerRoles();
    const setSellerRoles = useSellerRolesUpdater();

    const onUGCTranslateSuccess = useCallback(
        (
            {
                certifications = [],
                description = '',
                vacationMessage = '',
                notableClients: updatedNotableClients = [],
                roleFaqs = [],
                roleDescriptions = [],
            } = {} as any,
            showTranslatedUGC: boolean
        ) => {
            const existingCertifications = seller?.certifications;
            const updatedCertifications = Array.isArray(existingCertifications)
                ? existingCertifications.map((cert, i) => ({
                      ...cert,
                      certificationName: certifications[i],
                  }))
                : [];
            const notableClientsArr =
                seller?.notableClients && seller?.notableClients.length > 0 ? seller?.notableClients : [];
            setSeller({
                ...seller,
                vacation: {
                    ...(seller?.vacation || {}),
                    awayMessage: vacationMessage,
                },
                description,
                certifications: updatedCertifications,
                notableClients: notableClientsArr.map((client, i) => ({
                    ...client,
                    description: updatedNotableClients[i]?.description,
                    company: client.company && {
                        ...client.company,
                        description: updatedNotableClients[i]?.companyDescription,
                    },
                })),
            } as any);

            setSellerRoles(
                sellerRoles.map((sellerRole, index) => ({
                    ...sellerRole,
                    // Yea, this thing is "reliable as a Swiss watch".
                    // But at this point we cannot really translate UGCs on the fly,
                    // because this logic is tied deeply in seller_pages_toolbox/UGCTranslateButton which does it on mount only,
                    // and that's the reason we translate all per-role UGC data at once here
                    faq: roleFaqs[index],
                    description: roleDescriptions[index],
                }))
            );

            setShowTranslatedUGC(showTranslatedUGC);
        },
        [seller, setSeller, sellerRoles, setSellerRoles]
    );

    const getUgc = useCallback(() => {
        const notableClientsArr =
            seller?.notableClients && seller?.notableClients.length > 0 ? seller?.notableClients : [];

        const roleFaqs = sellerRoles ? sellerRoles.map(({ faq }) => faq) : [];
        const roleDescriptions = sellerRoles ? sellerRoles.map(({ description }) => description) : [];

        return {
            description: seller?.description,
            certifications: seller?.certifications?.map((cert) => cert.certificationName) || [],
            vacationMessage: seller?.vacation?.awayMessage,
            notableClients: (notableClientsArr as any).map((client) => ({
                description: client.description || '',
                companyDescription: client.company?.description || '',
            })),
            roleFaqs,
            roleDescriptions,
        };
    }, [seller, sellerRoles]);

    return {
        onUGCTranslateSuccess,
        getUgc,
        showTranslatedUGC,
    };
};

const selectSellerSlice = (seller: SellerPageFederatedSeller) => seller;
